import PropTypes from 'prop-types';
import { useState } from 'react';

import { ContainerFlex } from '../Containers';

const ALL = 'ALL';

const TableFilters = ({ data, filters, label, setFilteredData }) => {
  /* Help function:
  Filter data by filter values */
  const filterData = (filterValues) => {
    let filteredData = [...data];

    filters.forEach((filter) => {
      const { getValuePath, id } = filter;

      if (filterValues[id] !== ALL) {
        filteredData = filteredData.filter(
          (item) => getValuePath(item) === filterValues[id],
        );
      }
    });

    return filteredData;
  };

  /* Help function:
  Set all filters' initial value to ALL.
  Generate each filter's all unique values from the data; each key is the filter id.
  Example result:
  {
    defaultFilterValues : {
      people: ALL,
      status: ALL,
    },
    filterOptionValues: {
      people: [ 'User 1', 'User 2', 'User 3'],
      status: [ 'BUILDING', 'CANCELED', 'FAILED', 'SUCCESS'],
    },
  } */
  const getFilterOptionsAndValues = () => {
    const defaultFilterValues = {};
    const filterOptionValues = {};

    filters.forEach((filter) => {
      const { getValuePath, id } = filter;

      const uniqueValuesSet = new Set();
      data.forEach((item) => {
        uniqueValuesSet.add(getValuePath(item));
      });

      filterOptionValues[id] = Array.from(uniqueValuesSet);
      defaultFilterValues[id] = ALL;
    });

    return { defaultFilterValues, filterOptionValues };
  };
  // End of help functions

  const { defaultFilterValues, filterOptionValues } =
    getFilterOptionsAndValues();
  const [filterValues, setFilterValues] = useState(defaultFilterValues);

  const handleChange = (event) => {
    const { id, value } = event.target;

    // Only re-filter if the filter value has changed
    if (filterValues[id] !== value) {
      const newFilterValues = {
        ...filterValues,
        [id]: value,
      };

      setFilterValues(newFilterValues);
      setFilteredData(filterData(newFilterValues));
    }
  };

  return (
    <ContainerFlex>
      {label ? (
        <legend className="py-2 pl-0 pr-4 font-semibold">{label}</legend>
      ) : null}
      {filters.map((filter) => (
        <div className="w-40 pr-4 mb-4" key={filter.id}>
          <label className="sr-only" htmlFor={filter.id}>
            {filter.ariaLabel}
          </label>

          <select
            id={filter.id}
            name={filter.id}
            onBlur={handleChange}
            onChange={handleChange}
          >
            <option value={ALL}>{filter.optionAllText}</option>
            {filterOptionValues[filter.id].map((value) => (
              <option key={value} value={value}>
                {filter.optionValueToText
                  ? filter.optionValueToText(value)
                  : value}
              </option>
            ))}
          </select>
        </div>
      ))}
    </ContainerFlex>
  );
};

TableFilters.defaultProps = {
  label: '',
};

TableFilters.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      ariaLabel: PropTypes.string.isRequired,
      getValuePath: PropTypes.func.isRequired, // The path to get the value from data
      id: PropTypes.string.isRequired,
      optionAllText: PropTypes.string.isRequired,
      optionValueToText: PropTypes.func, // If not present, use the value for option text
    }).isRequired,
  ).isRequired,
  label: PropTypes.string,
  setFilteredData: PropTypes.func.isRequired,
};

export default TableFilters;
